/**
 * timelineService — Récupère et fusionne observations + traçabilités d'un patient
 */

import { apiClient } from './apiClient';
import { API_ENDPOINTS } from '@/config/api';
import type {
  Observation,
  Tracabilite,
  ObservationListResponse,
  TracabiliteListResponse,
  TimelineEntry,
} from '@/shared/types/observation';

function toTimelineEntry(item: Observation | Tracabilite, kind: 'observation' | 'tracabilite'): TimelineEntry {
  if (kind === 'observation') {
    const obs = item as Observation;
    return {
      id: obs.id,
      kind,
      type: obs.type,
      contenu_brut: obs.contenu_brut,
      contenu_structure: obs.contenu_structure,
      statut: obs.statut,
      auteur_id: obs.auteur_id,
      created_at: obs.created_at,
      raw: obs,
    };
  }
  const t = item as Tracabilite;
  return {
    id: t.id,
    kind,
    type: t.type_acte ?? 'administration_medicament',
    contenu_brut: t.contenu_brut ?? [t.medicament, t.dose, t.voie].filter(Boolean).join(' ') ?? '',
    contenu_structure: t.medicament
      ? { medicament: t.medicament, dose: t.dose, voie: t.voie, site: t.site, heure: t.heure_debut }
      : null,
    statut: t.statut,
    auteur_id: t.auteur_id,
    created_at: t.created_at,
    raw: t,
  };
}

export const timelineService = {
  async getTimeline(patientId: string, limit = 50): Promise<TimelineEntry[]> {
    const [obsRes, tracRes] = await Promise.all([
      apiClient.get<ObservationListResponse>(
        `${API_ENDPOINTS.observations}?patient_id=${patientId}&limit=${limit}`
      ),
      apiClient.get<TracabiliteListResponse>(
        `${API_ENDPOINTS.tracabilites}?patient_id=${patientId}&limit=${limit}`
      ),
    ]);

    const obsEntries = obsRes.items.map((o) => toTimelineEntry(o, 'observation'));
    const tracEntries = tracRes.items.map((t) => toTimelineEntry(t, 'tracabilite'));

    return [...obsEntries, ...tracEntries].sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
  },

  async validerObservation(observationId: string): Promise<Observation> {
    return apiClient.patch<Observation>(API_ENDPOINTS.observationValider(observationId), {});
  },
};
